<template>
  <div>
    <Menu />

    <section class="events-page">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-lg-8 text-center events-intro">
            <h1>LUXURY EVENTS</h1>
            <p>
              Discover a curated collection of exceptional occasions, private gatherings and memorable
              experiences brought together by Chas Everitt Luxury.
            </p>
          </div>
        </div>

        <div v-if="loading" class="events-loading text-center">
          <div class="spinner"></div>
          <h2>Loading luxury events</h2>
        </div>

        <div v-else-if="events.length === 0" class="text-center py-5">
          <h2>No events found</h2>
        </div>

        <div v-else class="events-list">
          <article
            v-for="item in events"
            :key="getEventRef(item)"
            class="event-card"
          >
            <div class="event-card__copy text-center">
              <router-link
                :to="'/events/' + getEventSlug(item)"
                class="event-card__title-link"
              >
                <h2>{{ getEventTitle(item) }}</h2>
              </router-link>
              <p v-if="getEventSummary(item)">{{ getEventSummary(item) }}</p>
            </div>

            <router-link
              :to="'/events/' + getEventSlug(item)"
              class="event-card__image-wrap d-block"
            >
              <img
                :src="getEventImage(item)"
                :alt="getEventTitle(item)"
                class="event-card__image"
              >

              <span class="event-card__date">
                {{ formatEventDate(getEventDate(item)) }}
              </span>

              <div v-if="getEventTags(item).length" class="event-card__tags">
                <span
                  v-for="tag in getEventTags(item)"
                  :key="tag"
                  class="event-card__tag"
                >
                  {{ tag }}
                </span>
              </div>
            </router-link>

            <div class="text-center event-card__action">
              <router-link
                :to="'/events/' + getEventSlug(item)"
                class="btn btn-primary"
              >
                FIND OUT MORE
              </router-link>
            </div>
          </article>
        </div>
      </div>
    </section>

    <Footer />
  </div>
</template>

<script>
import axios from 'axios'
import Menu from '@/components/home/Menu.vue'
import Footer from '@/components/home/Footer.vue'

const API_BASE = 'https://luxury-dev.chaseveritt.co.za/api/v3'
const IMAGE_BASE = API_BASE + '/'

export default {
  name: 'Events',

  components: {
    Menu,
    Footer
  },

  data() {
    return {
      events: [],
      loading: true
    }
  },

  mounted() {
    this.fetchEvents()
  },

  methods: {
    async fetchEvents() {
      this.loading = true

      try {
        const response = await axios.get(API_BASE + '/events.php', {
          params: {
            action: 'list',
            site: 'luxury',
            status: 'published'
          },
          headers: {
            Accept: 'application/json'
          }
        })

        if (
          response.data &&
          response.data.success === true &&
          Array.isArray(response.data.events)
        ) {
          this.events = response.data.events.filter(item => {
            const row = this.getEventRow(item)
            return row && row.site === 'luxury' && row.status === 'published'
          })
        } else {
          this.events = []
        }
      } catch (error) {
        console.log('Could not load events:', error)
        this.events = []
      }

      this.loading = false
    },

    getEventRow(item) {
      if (
        item &&
        item.event &&
        item.event.data &&
        item.event.data.event &&
        item.event.data.event[0]
      ) {
        return item.event.data.event[0]
      }

      return {}
    },

    getEventRef(item) {
      const row = this.getEventRow(item)
      return (item && item.meta && item.meta.ref_id) || row.ref_id || row.slug || row.title
    },

    getEventTitle(item) {
      const row = this.getEventRow(item)
      return row.title || 'Luxury Event'
    },

    getEventSlug(item) {
      const row = this.getEventRow(item)
      return row.slug || row.ref_id || this.slugify(this.getEventTitle(item))
    },

    getEventDate(item) {
      const row = this.getEventRow(item)
      return row.event_date || (item && item.meta ? item.meta.event_date : '') || ''
    },

    getEventSummary(item) {
      const row = this.getEventRow(item)
      const value = row.summary || row.description || ''
      return this.truncateText(this.stripHtml(value), 520)
    },

    getEventImage(item) {
      const row = this.getEventRow(item)
      let image = row.cover_image || ''

      if (!image && Array.isArray(row.image_gallery) && row.image_gallery.length) {
        image = row.image_gallery[0].url || ''
      }

      return this.resolveImageUrl(image)
    },

    getEventTags(item) {
      const row = this.getEventRow(item)
      const tags = row.tags || {}
      const labels = []

      if (tags.city) labels.push('CITY')
      if (tags.inland) labels.push('INLAND')
      if (tags.countryside) labels.push('COUNTRYSIDE')
      if (tags.coastal) labels.push('COASTAL')

      return labels
    },

    resolveImageUrl(value) {
      if (!value) return '/images/luxury-portfolio.png'
      if (value.indexOf('http://') === 0 || value.indexOf('https://') === 0) return value
      return IMAGE_BASE + value.replace(/^\/+/, '')
    },

    formatEventDate(value) {
      if (!value) return ''

      const parts = String(value).split('-')
      if (parts.length !== 3) return value

      const date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]))

      return new Intl.DateTimeFormat('en-ZA', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      }).format(date)
    },

    stripHtml(value) {
      return String(value || '').replace(/(<([^>]+)>)/gi, '').trim()
    },

    truncateText(value, length) {
      if (!value || value.length <= length) return value
      return value.substring(0, length).trim() + '...'
    },

    slugify(value) {
      if (!value) return 'event'

      return value
        .toString()
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
        .replace(/-+/g, '-')
    }
  }
}
</script>

<style scoped>
.events-page {
  padding: 68px 0 80px;
}

.events-intro {
  margin-bottom: 42px;
}

.events-intro h1,
.event-card__copy h2 {
  text-transform: uppercase;
}

.events-intro p {
  max-width: 820px;
  margin: 0 auto;
}

.events-list {
  max-width: 1140px;
  margin: 0 auto;
}

.event-card {
  margin-bottom: 54px;
}

.event-card__copy {
  max-width: 850px;
  margin: 0 auto 18px;
}

.event-card__copy h2 {
  margin-bottom: 8px;
}

.event-card__copy p {
  margin-bottom: 0;
}

.event-card__title-link {
  color: inherit;
  text-decoration: none;
}

.event-card__image-wrap {
  position: relative;
  overflow: visible;
}

.event-card__image {
  display: block;
  width: 100%;
  height: 315px;
  object-fit: cover;
  border-radius: 4px 4px 28px 4px;
}

.event-card__date {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
  min-width: 210px;
  padding: 12px 24px;
  background: #fff;
  color: #255699;
  font-weight: 700;
  text-align: center;
  border-radius: 0 0 5px 0;
}

.event-card__tags {
  position: absolute;
  right: 0;
  bottom: 0;
  z-index: 2;
  display: flex;
  align-items: flex-end;
  gap: 5px;
}

.event-card__tag {
  min-width: 110px;
  padding: 8px 18px;
  background: #123a69;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  text-align: center;
  letter-spacing: .04em;
}

.event-card__action {
  margin-top: 12px;
}

.event-card__action .btn {
  min-width: 180px;
  border-radius: 14px;
  padding: 7px 24px;
}

.events-loading {
  padding: 70px 0;
}

.spinner {
  border: 8px solid rgba(0, 0, 0, 0.1);
  border-left: 8px solid #3498db;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  animation: spin 1.2s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@media (max-width: 767px) {
  .events-page {
    padding-top: 38px;
  }

  .event-card {
    margin-bottom: 44px;
  }

  .event-card__image {
    height: 230px;
    border-radius: 4px 4px 20px 4px;
  }

  .event-card__date {
    min-width: 0;
    padding: 9px 14px;
    font-size: 12px;
  }

  .event-card__tags {
    gap: 2px;
  }

  .event-card__tag {
    min-width: 0;
    padding: 7px 9px;
    font-size: 10px;
  }
}
</style>
